import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { conversationService } from '../../../services/conversationService';
import { inboxService } from '../../../services/inboxService';
import { supportGroupService } from '../../../services/supportGroupService';
import { labelService } from '../../../services/labelService';
import { Conversation, ConversationFilters, getConversationConductor } from '../../../types/conversation';
import { SupportMessage } from '../../../types/supportMessage';
import { SupportGroup } from '../../../types/supportGroup';
import { ConversationLabel, Label } from '../../../types/label';
import { isLeadTemperatureCode } from '../../../utils/leadTemperatureDisplay';
import { Inbox } from '../../../types/inbox';
import {
  ConversationStatus,
  isConversationClosedForMessaging,
  normalizeConversationStatus,
  SupportMessageStatus,
  SupportMessageType,
  SupportSenderType,
} from '../../../types/atendimentoEnums';
import { WorkspaceView, createAllView } from '../../../types/workspaceView';
import { useToast } from '../../UI/ToastProvider';
import { useAuth } from '../../../contexts/AuthContext';
import { useConversationHub } from '../../../hooks/useConversationHub';
import { rememberHubSupportMessageId } from '../../../utils/supportHubPayload';
import { isUnreadIncomingMessage, toLastNonActivityPreview } from '../../../utils/conversationQueuePreview';
import { WorkspaceInboxFilterValue } from './WorkspaceInboxFilter';

const ACTIVE_STATUSES: ConversationStatus[] = [
  ConversationStatus.Open,
  ConversationStatus.Pending,
  ConversationStatus.WaitingForOperator,
  ConversationStatus.InProgress,
  ConversationStatus.Reopened,
];

const RESOLVED_STATUSES: ConversationStatus[] = [
  ConversationStatus.Resolved,
];

const CAPTURED_STATUSES: ConversationStatus[] = [
  ConversationStatus.Captured,
];

const ACTIVE_STATUS_SET = new Set(ACTIVE_STATUSES);

export type WorkspaceQueueScope = 'active' | 'resolved' | 'captured' | 'all';

export interface UseWorkspaceConversationsOptions {
  initialSelectedId?: string | null;
}

const sameId = (left?: string | null, right?: string | null): boolean => {
  if (!left || !right)
  {
    return false;
  }

  return left.toLowerCase() === right.toLowerCase();
};

const statusesForScope = (scope: WorkspaceQueueScope): ConversationStatus[] | undefined => {
  if (scope === 'resolved')
  {
    return RESOLVED_STATUSES;
  }

  if (scope === 'captured')
  {
    return CAPTURED_STATUSES;
  }

  if (scope === 'all')
  {
    return [...ACTIVE_STATUSES, ...RESOLVED_STATUSES, ...CAPTURED_STATUSES];
  }

  return ACTIVE_STATUSES;
};

const belongsToScope = (status: ConversationStatus, scope: WorkspaceQueueScope): boolean => {
  if (scope === 'all')
  {
    return ACTIVE_STATUS_SET.has(status)
      || status === ConversationStatus.Resolved
      || status === ConversationStatus.Captured;
  }

  if (scope === 'resolved')
  {
    return status === ConversationStatus.Resolved;
  }

  if (scope === 'captured')
  {
    return status === ConversationStatus.Captured;
  }

  return ACTIVE_STATUS_SET.has(status);
};

type PendingReadSnapshot = {
  unreadCount: number;
  agentLastSeenAt?: string | null;
  assigneeLastSeenAt?: string | null;
  lastNonActivityMessage?: Conversation['lastNonActivityMessage'];
};

const overlayPendingRead = (
  conversation: Conversation,
  pending: PendingReadSnapshot | undefined,
): Conversation => {
  if (!pending)
  {
    return conversation;
  }

  return {
    ...conversation,
    unreadCount: pending.unreadCount,
    agentLastSeenAt: pending.agentLastSeenAt,
    assigneeLastSeenAt: pending.assigneeLastSeenAt,
    lastNonActivityMessage: pending.lastNonActivityMessage,
  };
};

const overlayOptimisticRead = (
  conversation: Conversation,
  optimisticIds: Set<string>,
): Conversation => {
  if (!optimisticIds.has(conversation.id))
  {
    return conversation;
  }

  return {
    ...conversation,
    unreadCount: 0,
  };
};

const withMessagePreview = (conversation: Conversation, message: SupportMessage): Conversation => {
  const preview = toLastNonActivityPreview(message, conversation.customAttributes);
  return {
    ...conversation,
    lastActivityAt: message.createdAt || conversation.lastActivityAt,
    lastNonActivityMessage: preview ?? conversation.lastNonActivityMessage,
  };
};

const mergeConversationUpdate = (current: Conversation, updated: Conversation): Conversation => {
  return {
    ...current,
    ...updated,
    contactName: updated.contactName || current.contactName,
    inboxName: updated.inboxName || current.inboxName,
    contactEmail: updated.contactEmail ?? current.contactEmail,
    contactPhone: updated.contactPhone ?? current.contactPhone,
    contactIdentifier: updated.contactIdentifier ?? current.contactIdentifier,
    assigneeName: updated.assigneeName ?? current.assigneeName,
    teamName: updated.teamName ?? current.teamName,
    supportGroupName: updated.supportGroupName ?? current.supportGroupName,
    labels: updated.labels ?? current.labels,
    classificationBadges: updated.classificationBadges ?? current.classificationBadges,
    lastNonActivityMessage: updated.lastNonActivityMessage ?? current.lastNonActivityMessage,
  };
};

const isOperatorLabel = (label: Pick<Label, 'name'>): boolean => !isLeadTemperatureCode(label.name);

const isOperatorConversationLabel = (
  item: Pick<ConversationLabel, 'labelName'>,
): boolean => !isLeadTemperatureCode(item.labelName);

const appendMessage = (prev: SupportMessage[], message: SupportMessage): SupportMessage[] => {
  const index = prev.findIndex((item) => sameId(item.id, message.id));
  if (index >= 0)
  {
    const next = [...prev];
    next[index] = message;
    return next;
  }

  return [...prev, message];
};

const isOptimisticOutgoingMatch = (local: SupportMessage, incoming: SupportMessage): boolean => {
  if (!sameId(local.conversationId, incoming.conversationId))
  {
    return false;
  }

  if (local.isPrivate || incoming.isPrivate)
  {
    return false;
  }

  if (local.messageType !== SupportMessageType.Outgoing
    || incoming.messageType !== SupportMessageType.Outgoing)
  {
    return false;
  }

  if (local.senderType !== incoming.senderType)
  {
    return false;
  }

  return (local.content ?? '') === (incoming.content ?? '');
};

export const useWorkspaceConversations = (options?: UseWorkspaceConversationsOptions) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const initialSelectedId = options?.initialSelectedId ?? null;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [availableLabels, setAvailableLabels] = useState<Label[]>([]);
  const [conversationLabels, setConversationLabels] = useState<ConversationLabel[]>([]);
  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [labelUpdating, setLabelUpdating] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [inboxFilter, setInboxFilter] = useState<WorkspaceInboxFilterValue>({ kind: 'all' });
  const [accessibleInboxes, setAccessibleInboxes] = useState<Inbox[]>([]);
  const [queueScope, setQueueScope] = useState<WorkspaceQueueScope>('active');
  const [activeView, setActiveView] = useState<WorkspaceView>(createAllView());
  const selectedIdRef = useRef<string | null>(selectedId);
  const sendingRef = useRef(false);
  const pendingOptimisticIdRef = useRef<string | null>(null);
  const retryingRef = useRef<string | null>(null);
  const pendingReadsRef = useRef<Map<string, PendingReadSnapshot>>(new Map());
  const visibleReadsRef = useRef<Set<string>>(new Set());
  const optimisticReadRef = useRef<Set<string>>(new Set());
  const markingReadRef = useRef<string | null>(null);
  const conversationsRef = useRef<Conversation[]>([]);
  selectedIdRef.current = selectedId;
  conversationsRef.current = conversations;

  useEffect(() => {
    if (!initialSelectedId)
    {
      return;
    }

    setSelectedId(initialSelectedId);
  }, [initialSelectedId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  useEffect(() => {
    const loadInboxes = async () => {
      try
      {
        const data = await inboxService.getAll();
        setAccessibleInboxes(data.filter((inbox) => inbox.isActive));
      }
      catch
      {
        addToast('Erro ao carregar caixas de entrada', 'error');
      }
    };

    void loadInboxes();
  }, [addToast]);

  const accessibleInboxIds = useMemo(
    () => new Set(accessibleInboxes.map((inbox) => inbox.id)),
    [accessibleInboxes],
  );

  const isConversationAccessible = useCallback((conversation: Conversation): boolean => {
    return accessibleInboxIds.has(conversation.inboxId);
  }, [accessibleInboxIds]);

  useEffect(() => {
    const loadGroups = async () => {
      try
      {
        const data = await supportGroupService.getAll();
        setGroups(data.filter((group) => group.isActive));
      }
      catch
      {
        addToast('Erro ao carregar grupos', 'error');
      }
    };

    void loadGroups();
  }, [addToast]);

  useEffect(() => {
    const loadLabels = async () => {
      try
      {
        const data = await labelService.getAll();
        setAvailableLabels(data.filter((label) => label.isActive && isOperatorLabel(label)));
      }
      catch
      {
        addToast('Erro ao carregar etiquetas', 'error');
      }
    };

    void loadLabels();
  }, [addToast]);

  const applyConversationUpdate = useCallback((updated: Conversation) => {
    const pending = pendingReadsRef.current.get(updated.id);
    const normalized: Conversation = overlayOptimisticRead(
      overlayPendingRead({
        ...updated,
        status: normalizeConversationStatus(updated.status),
      }, pending),
      optimisticReadRef.current,
    );

    setSelected((current) => (current && current.id === normalized.id
      ? mergeConversationUpdate(current, normalized)
      : normalized));
    setSelectedGroupId(normalized.supportGroupId ?? '');

    if (normalized.labels)
    {
      setConversationLabels(normalized.labels
        .filter(isOperatorLabel)
        .map((label) => ({
          id: label.id,
          labelId: label.id,
          labelName: label.name,
          labelColor: label.color,
          labelDescription: label.description,
          appliedByBot: false,
          createdAt: label.createdAt,
        })));
    }

    if (!belongsToScope(normalized.status, queueScope))
    {
      setConversations((prev) => {
        const remaining = prev.filter((item) => item.id !== normalized.id);
        setSelectedId((current) => {
          if (current !== normalized.id)
          {
            return current;
          }

          return remaining[0]?.id ?? null;
        });
        return remaining;
      });
      return;
    }

    setConversations((prev) => {
      const existing = prev.find((item) => item.id === normalized.id);
      if (!existing)
      {
        return [normalized, ...prev];
      }

      const merged = mergeConversationUpdate(existing, normalized);
      return prev.map((item) => (item.id === normalized.id ? merged : item));
    });
  }, [queueScope]);

  const holdUnreadUntilVisible = useCallback((conversation: Conversation) => {
    if ((conversation.unreadCount ?? 0) <= 0)
    {
      return;
    }

    if (visibleReadsRef.current.has(conversation.id))
    {
      return;
    }

    pendingReadsRef.current.set(conversation.id, {
      unreadCount: conversation.unreadCount ?? 0,
      agentLastSeenAt: conversation.agentLastSeenAt,
      assigneeLastSeenAt: conversation.assigneeLastSeenAt,
      lastNonActivityMessage: conversation.lastNonActivityMessage,
    });

    setConversations((prev) => prev.map((item) => (
      sameId(item.id, conversation.id)
        ? overlayPendingRead(item, pendingReadsRef.current.get(item.id))
        : item
    )));
  }, []);

  const acknowledgeTranscriptVisible = useCallback((conversationId: string) => {
    visibleReadsRef.current.add(conversationId);
    pendingReadsRef.current.delete(conversationId);

    const applyZero = (item: Conversation): Conversation => {
      if (!sameId(item.id, conversationId))
      {
        return item;
      }

      return {
        ...item,
        unreadCount: 0,
      };
    };

    setConversations((prev) => prev.map(applyZero));
    setSelected((current) => (current && sameId(current.id, conversationId) ? applyZero(current) : current));
  }, []);

  const acknowledgeActiveRead = useCallback(() => {
    const conversationId = selectedIdRef.current;
    if (!conversationId)
    {
      return;
    }

    const listUnread = conversationsRef.current.find((item) => sameId(item.id, conversationId))?.unreadCount ?? 0;
    if (listUnread <= 0)
    {
      return;
    }

    if (markingReadRef.current === conversationId)
    {
      return;
    }

    markingReadRef.current = conversationId;
    optimisticReadRef.current.add(conversationId);
    pendingReadsRef.current.delete(conversationId);

    const applyZero = (item: Conversation): Conversation => {
      if (!sameId(item.id, conversationId))
      {
        return item;
      }

      return {
        ...item,
        unreadCount: 0,
      };
    };

    setConversations((prev) => prev.map(applyZero));
    setSelected((current) => (current && sameId(current.id, conversationId) ? applyZero(current) : current));

    void conversationService.markLastSeen(conversationId)
      .catch(() => {
        // last-seen is best-effort while the operator is already in the transcript
      })
      .finally(() => {
        if (markingReadRef.current === conversationId)
        {
          markingReadRef.current = null;
        }
      });
  }, []);

  const clearPendingRead = useCallback((conversationId: string) => {
    pendingReadsRef.current.delete(conversationId);
  }, []);

  const markLastSeenOnOpen = useCallback(async (conversation: Conversation) => {
    try
    {
      await conversationService.markLastSeen(conversation.id);
    }
    catch
    {
      // last-seen is best-effort; opening the transcript must not fail
    }
  }, []);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try
    {
      const filters: ConversationFilters = {
        statuses: statusesForScope(queueScope),
        search: debouncedSearch || undefined,
      };

      if (inboxFilter.kind === 'channel')
      {
        filters.channelType = inboxFilter.channelType;
      }
      else if (inboxFilter.kind === 'inbox')
      {
        filters.inboxId = inboxFilter.inboxId;
      }

      if (activeView.type === 'mine')
      {
        filters.assigneeId = user?.id;
      }
      else if (activeView.type === 'group' && activeView.supportGroupId)
      {
        filters.supportGroupId = activeView.supportGroupId;
      }

      const data = await conversationService.getAll(filters);
      setConversations(data.map((item) => overlayOptimisticRead(
        overlayPendingRead(item, pendingReadsRef.current.get(item.id)),
        optimisticReadRef.current,
      )));

      setSelectedId((current) => {
        if (current && data.some((item) => item.id === current))
        {
          return current;
        }

        return data[0]?.id ?? null;
      });
    }
    catch
    {
      addToast('Erro ao carregar fila de atendimento', 'error');
    }
    finally
    {
      setListLoading(false);
    }
  }, [addToast, debouncedSearch, inboxFilter, queueScope, activeView, user?.id]);

  const loadDetail = useCallback(async (id: string) => {
    visibleReadsRef.current.delete(id);
    optimisticReadRef.current.delete(id);
    if (markingReadRef.current === id)
    {
      markingReadRef.current = null;
    }
    clearPendingRead(id);
    setDetailLoading(true);
    try
    {
      const [conversation, messagesData, labelsData] = await Promise.all([
        conversationService.getById(id),
        conversationService.getMessages(id),
        conversationService.getLabels(id),
      ]);
      setSelected(conversation);
      setSelectedGroupId(conversation.supportGroupId ?? '');
      setMessages(messagesData);
      setConversationLabels(labelsData.filter(isOperatorConversationLabel));
      setSelectedLabelId('');
      setDraft('');
      holdUnreadUntilVisible(conversation);
      void markLastSeenOnOpen(conversation);
    }
    catch
    {
      addToast('Erro ao carregar conversa', 'error');
      setSelected(null);
      setMessages([]);
      setConversationLabels([]);
    }
    finally
    {
      setDetailLoading(false);
    }
  }, [addToast, clearPendingRead, holdUnreadUntilVisible, markLastSeenOnOpen]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (!selectedId)
    {
      setSelected(null);
      setMessages([]);
      setConversationLabels([]);
      return;
    }

    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useConversationHub({
    conversationId: selectedId,
    callbacks: {
      onConversationListChanged: ({ conversationId }) => {
        void loadList();

        const currentSelectedId = selectedIdRef.current;
        if (!currentSelectedId || !sameId(conversationId, currentSelectedId))
        {
          return;
        }

        void conversationService.getMessages(currentSelectedId)
          .then((messagesData) => {
            if (!sameId(selectedIdRef.current, currentSelectedId))
            {
              return;
            }

            setMessages(messagesData);
          })
          .catch(() => {
            // best-effort refresh while conversation stays open
          });

        void conversationService.getLabels(currentSelectedId)
          .then((labelsData) => {
            if (!sameId(selectedIdRef.current, currentSelectedId))
            {
              return;
            }

            setConversationLabels(labelsData.filter(isOperatorConversationLabel));
          })
          .catch(() => {
            // best-effort refresh while conversation stays open
          });
      },
      onConversationUpdated: (updated) => {
        if (!isConversationAccessible(updated))
        {
          return;
        }

        const pending = pendingReadsRef.current.get(updated.id);
        const next = overlayOptimisticRead(
          overlayPendingRead(updated, pending),
          optimisticReadRef.current,
        );

        if (sameId(next.id, selectedIdRef.current))
        {
          applyConversationUpdate(next);
        }
        else
        {
          setConversations((prev) => prev.map((item) => (
            sameId(item.id, next.id) ? mergeConversationUpdate(item, next) : item
          )));
        }
      },
      onConversationDeleted: ({ conversationId }) => {
        setConversations((prev) => prev.filter((item) => !sameId(item.id, conversationId)));
        if (sameId(selectedIdRef.current, conversationId))
        {
          setSelectedId(null);
          setSelected(null);
          setMessages([]);
          setConversationLabels([]);
        }
      },
      onMessageReceived: (message) => {
        const isSelected = sameId(message.conversationId, selectedIdRef.current);
        const isIncomingUnread = isUnreadIncomingMessage(message);
        if (isSelected && isIncomingUnread)
        {
          optimisticReadRef.current.delete(message.conversationId);
          if (markingReadRef.current === message.conversationId)
          {
            markingReadRef.current = null;
          }
        }

        if (isSelected)
        {
          setMessages((prev) => {
            const optimisticId = pendingOptimisticIdRef.current;
            if (optimisticId)
            {
              const local = prev.find((item) => sameId(item.id, optimisticId));
              if (local && isOptimisticOutgoingMatch(local, message))
              {
                pendingOptimisticIdRef.current = null;
                const withoutLocal = prev.filter((item) => !sameId(item.id, optimisticId));
                return appendMessage(withoutLocal, message);
              }
            }

            return appendMessage(prev, message);
          });
          if (isIncomingUnread)
          {
            setSelected((current) => {
              if (!current || !sameId(current.id, message.conversationId))
              {
                return current;
              }

              const withPreview = withMessagePreview(current, message);
              return {
                ...withPreview,
                unreadCount: (current.unreadCount ?? 0) + 1,
              };
            });
          }
        }

        setConversations((prev) => prev.map((item) => {
          if (!sameId(item.id, message.conversationId))
          {
            return item;
          }

          const withPreview = withMessagePreview(item, message);
          if (!isIncomingUnread)
          {
            return withPreview;
          }

          return {
            ...withPreview,
            unreadCount: (item.unreadCount ?? 0) + 1,
          };
        }));
      },
      onMessageStatusChanged: (payload) => {
        if (!sameId(payload.conversationId, selectedIdRef.current))
        {
          return;
        }

        setMessages((prev) => prev.map((item) => {
          if (!sameId(item.id, payload.messageId))
          {
            return item;
          }

          return {
            ...item,
            status: payload.status,
            contentAttributes: payload.contentAttributes ?? item.contentAttributes,
          };
        }));
      },
    },
  });

  const selectConversation = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedId(null);
  }, []);

  const sendMessage = useCallback(async (
    messageContent?: string,
    contentAttributes?: string,
  ) => {
    const content = (messageContent ?? draft).trim();
    if (!selectedId || !content || sendingRef.current)
    {
      return;
    }

    if (selected && isConversationClosedForMessaging(selected.status))
    {
      addToast(
        'Não é possível enviar mensagens em um atendimento finalizado. Reabra o atendimento para continuar.',
        'error',
      );
      return;
    }

    sendingRef.current = true;
    setSending(true);
    setDraft('');
    acknowledgeActiveRead();
    const localId = crypto.randomUUID();
    pendingOptimisticIdRef.current = localId;
    const optimistic: SupportMessage = {
      id: localId,
      conversationId: selectedId,
      messageType: SupportMessageType.Outgoing,
      content,
      senderType: SupportSenderType.AppUser,
      senderId: user?.id ?? null,
      senderName: user?.name ?? null,
      isPrivate: false,
      status: SupportMessageStatus.Pending,
      contentAttributes: contentAttributes ?? null,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    try
    {
      const created = await conversationService.sendMessage(selectedId, {
        messageType: SupportMessageType.Outgoing,
        content,
        senderType: SupportSenderType.AppUser,
        senderId: user?.id,
        contentAttributes,
      });
      rememberHubSupportMessageId(created.id);
      pendingOptimisticIdRef.current = null;
      setMessages((prev) => {
        const withoutLocal = prev.filter((item) => !sameId(item.id, localId));
        return appendMessage(withoutLocal, created);
      });
      void loadList();
    }
    catch (error)
    {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      const detail = axiosError.response?.data?.detail;
      addToast(detail || 'Erro ao enviar mensagem', 'error');
      pendingOptimisticIdRef.current = null;
      setMessages((prev) => prev.filter((item) => !sameId(item.id, localId)));
      setDraft((current) => {
        if (current.trim().length > 0)
        {
          return current;
        }

        return content;
      });
    }
    finally
    {
      sendingRef.current = false;
      setSending(false);
    }
  }, [selectedId, selected, draft, user?.id, user?.name, addToast, loadList, acknowledgeActiveRead]);

  const retryMessage = useCallback(async (messageId: string) => {
    if (!selectedId || retryingRef.current)
    {
      return;
    }

    if (selected && isConversationClosedForMessaging(selected.status))
    {
      addToast(
        'Não é possível enviar mensagens em um atendimento finalizado. Reabra o atendimento para continuar.',
        'error',
      );
      return;
    }

    retryingRef.current = messageId;
    setRetryingMessageId(messageId);
    try
    {
      const updated = await conversationService.retryMessage(selectedId, messageId);
      setMessages((prev) => appendMessage(prev, updated));
    }
    catch (error)
    {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      const detail = axiosError.response?.data?.detail;
      addToast(detail || 'Erro ao reenviar mensagem', 'error');
    }
    finally
    {
      retryingRef.current = null;
      setRetryingMessageId(null);
    }
  }, [selectedId, selected, addToast]);

  const markAsRead = useCallback(async () => {
    if (!selectedId)
    {
      return;
    }

    clearPendingRead(selectedId);
    setActionLoading(true);
    try
    {
      const updated = await conversationService.markLastSeen(selectedId);
      applyConversationUpdate(updated);
    }
    catch
    {
      addToast('Erro ao marcar conversa como lida', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, addToast, applyConversationUpdate, clearPendingRead]);

  const markAsUnread = useCallback(async () => {
    if (!selectedId)
    {
      return;
    }

    clearPendingRead(selectedId);
    optimisticReadRef.current.delete(selectedId);
    if (markingReadRef.current === selectedId)
    {
      markingReadRef.current = null;
    }
    setActionLoading(true);
    try
    {
      const updated = await conversationService.markUnread(selectedId);
      applyConversationUpdate(updated);
    }
    catch
    {
      addToast('Erro ao marcar conversa como não lida', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, addToast, applyConversationUpdate, clearPendingRead]);

  const assumeConversation = useCallback(async () => {
    if (!selectedId)
    {
      return;
    }

    setActionLoading(true);
    try
    {
      const updated = await conversationService.assume(selectedId);
      applyConversationUpdate(updated);
      addToast('Conversa assumida', 'success');
    }
    catch
    {
      addToast('Erro ao assumir conversa', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, addToast, applyConversationUpdate]);

  const updateStatus = useCallback(async (status: ConversationStatus) => {
    if (!selectedId)
    {
      return;
    }

    setActionLoading(true);
    try
    {
      const updated = await conversationService.updateStatus(selectedId, { status });
      applyConversationUpdate(updated);
      addToast(
        status === ConversationStatus.Resolved
          ? 'Atendimento finalizado'
          : status === ConversationStatus.Captured
            ? 'Conversa capturada'
            : 'Status atualizado',
        'success',
      );
    }
    catch
    {
      addToast('Erro ao atualizar status', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, addToast, applyConversationUpdate]);

  const resolveAndNext = useCallback(async () => {
    await updateStatus(ConversationStatus.Resolved);
  }, [updateStatus]);

  const handoffToHuman = useCallback(async () => {
    if (!selectedId)
    {
      return;
    }

    setActionLoading(true);
    try
    {
      const updated = await conversationService.handoffToHuman(selectedId, {
        supportGroupId: selectedGroupId || selected?.supportGroupId || undefined,
        reason: 'Encaminhamento manual para operador',
      });
      applyConversationUpdate(updated);
      addToast('Conversa encaminhada para atendimento humano', 'success');
    }
    catch
    {
      addToast('Erro ao encaminhar para humano', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, selectedGroupId, selected?.supportGroupId, addToast, applyConversationUpdate]);

  const handoffToBot = useCallback(async () => {
    if (!selectedId)
    {
      return;
    }

    setActionLoading(true);
    try
    {
      const updated = await conversationService.handoffToBot(selectedId, {
        reason: 'Conversa devolvida ao agente de IA',
      });
      applyConversationUpdate(updated);
      addToast('Conversa devolvida ao bot', 'success');
    }
    catch
    {
      addToast('Erro ao devolver ao bot', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, addToast, applyConversationUpdate]);

  const transferGroup = useCallback(async () => {
    if (!selectedId || !selectedGroupId)
    {
      return;
    }

    if (selected?.supportGroupId === selectedGroupId)
    {
      addToast('A conversa já está neste grupo', 'error');
      return;
    }

    setActionLoading(true);
    try
    {
      const updated = await conversationService.transferGroup(selectedId, {
        supportGroupId: selectedGroupId,
      });
      applyConversationUpdate(updated);
      addToast('Grupo atualizado', 'success');
    }
    catch
    {
      addToast('Erro ao transferir grupo', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, selectedGroupId, selected?.supportGroupId, addToast, applyConversationUpdate]);

  const reopenConversation = useCallback(async () => {
    if (!selectedId)
    {
      return;
    }

    setActionLoading(true);
    try
    {
      const updated = await conversationService.reopen(selectedId);
      applyConversationUpdate(updated);
      setSelectedId(updated.id);
      addToast('Conversa reaberta', 'success');
      void loadList();
    }
    catch
    {
      addToast('Erro ao reabrir conversa', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, addToast, applyConversationUpdate, loadList]);

  const deleteConversation = useCallback(async () => {
    if (!selectedId)
    {
      return;
    }

    const deletedId = selectedId;
    const remaining = conversations.filter((item) => item.id !== deletedId);
    const nextId = remaining[0]?.id ?? null;

    setActionLoading(true);
    try
    {
      await conversationService.delete(deletedId);
      setConversations(remaining);
      setSelectedId(nextId);
      if (!nextId)
      {
        setSelected(null);
        setMessages([]);
        setConversationLabels([]);
      }

      addToast('Conversa excluída', 'success');
    }
    catch
    {
      addToast('Erro ao excluir conversa', 'error');
    }
    finally
    {
      setActionLoading(false);
    }
  }, [selectedId, conversations, addToast]);

  const refreshLabels = useCallback(async () => {
    if (!selectedId)
    {
      return;
    }

    const [updatedConversation, updatedLabels] = await Promise.all([
      conversationService.getById(selectedId),
      conversationService.getLabels(selectedId),
    ]);
    applyConversationUpdate(updatedConversation);
    setConversationLabels(updatedLabels);
  }, [selectedId, applyConversationUpdate]);

  const applyLabel = useCallback(async () => {
    if (!selectedId || !selectedLabelId)
    {
      return;
    }

    if (conversationLabels.some((item) => item.labelId === selectedLabelId))
    {
      addToast('Etiqueta já aplicada', 'error');
      return;
    }

    setLabelUpdating(true);
    try
    {
      await conversationService.applyLabels(selectedId, { labelIds: [selectedLabelId] });
      await refreshLabels();
      setSelectedLabelId('');
      addToast('Etiqueta aplicada', 'success');
    }
    catch
    {
      addToast('Erro ao aplicar etiqueta', 'error');
    }
    finally
    {
      setLabelUpdating(false);
    }
  }, [selectedId, selectedLabelId, conversationLabels, addToast, refreshLabels]);

  const removeLabel = useCallback(async (labelId: string) => {
    if (!selectedId)
    {
      return;
    }

    setLabelUpdating(true);
    try
    {
      await conversationService.removeLabels(selectedId, { labelIds: [labelId] });
      await refreshLabels();
      addToast('Etiqueta removida', 'success');
    }
    catch
    {
      addToast('Erro ao remover etiqueta', 'error');
    }
    finally
    {
      setLabelUpdating(false);
    }
  }, [selectedId, addToast, refreshLabels]);

  const selectedInboxHasActiveBot = useMemo(() => {
    if (!selected)
    {
      return false;
    }

    const inbox = accessibleInboxes.find((item) => sameId(item.id, selected.inboxId));
    return Boolean(inbox?.agentBotId);
  }, [selected, accessibleInboxes]);

  const conductor = useMemo(
    () => (selected ? getConversationConductor(selected, selectedInboxHasActiveBot) : null),
    [selected, selectedInboxHasActiveBot],
  );

  const canAssume = conductor === 'waiting' || (conductor === 'bot' && !selected?.assigneeId);
  const canHandoffToHuman = conductor === 'bot';
  const canHandoffToBot = selectedInboxHasActiveBot && (
    conductor === 'waiting'
    || (conductor === 'human' && !!selected?.assigneeId)
  );
  const canCapture = selected?.status !== ConversationStatus.Captured;
  const canReopen = selected?.status === ConversationStatus.Resolved
    || selected?.status === ConversationStatus.Captured;

  const unappliedLabels = useMemo(
    () => availableLabels.filter(
      (label) => isOperatorLabel(label)
        && !conversationLabels.some((item) => item.labelId === label.id),
    ),
    [availableLabels, conversationLabels],
  );

  return {
    conversations,
    selectedId,
    selected,
    messages,
    groups,
    selectedGroupId,
    setSelectedGroupId,
    conversationLabels,
    selectedLabelId,
    setSelectedLabelId,
    unappliedLabels,
    labelUpdating,
    listLoading,
    detailLoading,
    sending,
    retryingMessageId,
    actionLoading,
    draft,
    setDraft,
    searchInput,
    setSearchInput,
    accessibleInboxes,
    inboxFilter,
    setInboxFilter,
    queueScope,
    setQueueScope,
    activeView,
    setActiveView,
    conductor,
    currentUserId: user?.id ?? null,
    canAssume,
    canHandoffToHuman,
    canHandoffToBot,
    canCapture,
    canReopen,
    selectConversation,
    clearSelection,
    sendMessage,
    retryMessage,
    assumeConversation,
    markAsRead,
    markAsUnread,
    acknowledgeTranscriptVisible,
    acknowledgeActiveRead,
    resolveAndNext,
    updateStatus,
    handoffToHuman,
    handoffToBot,
    transferGroup,
    reopenConversation,
    deleteConversation,
    applyLabel,
    removeLabel,
    reloadList: loadList,
  };
};
